import { useEffect } from 'react';
import './api/domino';
import './api/live';
import { game } from './api/game';
import { Avatar, Spinner, Toasts } from './components/common';
import { useT } from './i18n';
import { DominoGame } from './screens/DominoGame';
import { Invited } from './screens/Invited';
import { LiveRoom } from './screens/LiveRoom';
import { LiveStart } from './screens/LiveStart';
import { Login } from './screens/Login';
import { Onboarding } from './screens/Onboarding';
import { Portal } from './screens/Portal';
import { Services } from './screens/Services';
import { Watch } from './screens/Watch';
import { useSession } from './store/session';
import { useUi } from './store/ui';
import { Table } from './table/Table';
import { tg } from './tg/bridge';

/** "Winner … hit the jackpot and got ❤1000": shown to everyone for a few seconds. */
function WinnerBanner() {
  const t = useT();
  const winner = useUi((s) => s.winner);
  if (!winner) return null;
  return (
    <div className="winner-banner" role="status" data-testid="winner-banner">
      <span className="winner-photo">
        <Avatar name={winner.name} photoUrl={winner.photoUrl} size="100%" />
      </span>
      <span>
        <b>{t('wheel.winner')}</b>
        <span>
          <em>{winner.name}</em>{' '}
          {winner.prize === 'jackpot'
            ? t('wheel.jackpot_text')
            : t('wheel.big_win_text', { prize: t(`wheel.prize.${winner.prize}`) })}
        </span>
      </span>
    </div>
  );
}

export function App() {
  const t = useT();
  const status = useSession((s) => s.status);
  const error = useSession((s) => s.error);
  const me = useSession((s) => s.me);
  const screen = useSession((s) => s.stack[s.stack.length - 1]!);
  const replaced = useSession((s) => s.replaced);
  const banned = useSession((s) => s.banned);

  // The portal pages are dark like the reference's site; Telegram's header follows.
  const dark = status === 'login' || (status === 'ready' && screen.name !== 'table');
  useEffect(() => {
    document.documentElement.dataset.page = dark ? 'portal' : 'game';
    tg.setColors(dark ? '#111827' : '#b8743b');
  }, [dark]);

  let content;
  if (status === 'booting') {
    content = (
      <div className="game">
        <div className="board">
          <div className="center board-center">
            <Spinner />
          </div>
        </div>
      </div>
    );
  } else if (status === 'login') {
    content = <Login />;
  } else if (status === 'error' || !me) {
    content = (
      <div className="center">
        <div>
          <div style={{ fontSize: 48 }} aria-hidden>
            🍾
          </div>
          <p>{t(error ?? 'errors.INTERNAL_ERROR')}</p>
        </div>
      </div>
    );
  } else if (!me.onboarded) {
    content = <Onboarding />;
  } else {
    switch (screen.name) {
      case 'portal':
        content = <Portal />;
        break;
      case 'services':
        content = <Services />;
        break;
      case 'invited':
        content = <Invited />;
        break;
      case 'watch':
        content = <Watch tab={screen.tab} />;
        break;
      case 'dominoRoom':
        content = (
          <div className="rooms">
            <DominoGame key={screen.id} id={screen.id} />
          </div>
        );
        break;
      case 'liveStart':
        content = (
          <div className="rooms">
            <LiveStart />
          </div>
        );
        break;
      case 'liveRoom':
        content = (
          <div className="rooms">
            <LiveRoom key={screen.id} id={screen.id} />
          </div>
        );
        break;
      default:
        content = <Table />;
    }
  }

  return (
    <div className="app">
      {content}
      <Toasts />
      <WinnerBanner />
      {banned && (
        <div className="notice-layer">
          <div className="notice">
            <p>{t('errors.BANNED')}</p>
          </div>
        </div>
      )}
      {replaced && !banned && (
        <div className="notice-layer">
          <div className="notice">
            <p>{t('table.session_replaced')}</p>
            <button className="btn btn-green" onClick={() => game.connect()}>
              {t('table.play_here')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
